import PropTypes from 'prop-types';
import get from 'lodash/get';
import { capitalize } from "lodash";

import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import useUser from '../../hooks/useUser';
import CreateConsolidatedFileButton from '../patients/CreateConsolidatedFileButton';
import SessionFilters, { SortButton } from './SessionFilters';
import SessionsTable from './SessionsTable';
import CustomAccordionStatusFilter from "../common/CustomAccordionStatusFilter";
import SessionStatus, { getStatusOptions } from "./SessionStatus";
import LoadingScreen from '../common/LoadingScreen';
import useIsMobile from '../../hooks/useIsMobile';
import './ListSessionsScreen.scss'

const TableView = props => {
  if (props.isLoading) return <LoadingScreen />;
  return (
    <Box>
      <SessionsTable
        {...props}
        rows={(props.sessions || []).map(s => ({ data: s }))}
      />
    </Box>
  );
};

const SessionStatusView = props => {
  const { tab, tabs } = props;
  const tabFilters = !props.isPatient ? tabs : tabs.filter(t => ['all', 'completed', 'upcoming'].includes(t)).sort();

  return <Stack
    className='session-status__box'
    direction={{ xs: 'column', sm: 'row' }}
    alignItems={{ xs: 'start', sm: 'center' }}
    columns={{ xs: 1 }}
    columnGap={{md: 4.25, lg: 8.75}}
    rowGap={{ xs: 0, md: 2 }}
    padding={{ xs: 0, md: 1 }}
    useFlexGap
  >
    {tabFilters
      .map(t =>
        <div
          className={`session-status-item-box ${t === tab && 'session-status-item-box__active'}`}
          onClick={() => { props.onTabChange(t) }}
        >
          <SessionStatus
            status={t}
            active={t === tab}
            statusCount={props.counts[t]}
          />
        </div>
      )
    }
  </Stack>
}

const ListSessionsScreen = props => {
  const { tab } = props;
  const tabName = tab !== 'all' ? capitalize(tab) : 'All Notes'
  const { color, Icon } = getStatusOptions(tabName);
  const isMobile = useIsMobile();

  return (
    <Box className="list-session" marginTop='12px'>
      <SessionFilters
        filters={props.filters}
        isPatient={props.isPatient}
        onFilterChange={props.onFilterChange}
      />
      <Card className="list-session__card drop-shadow">
        <CardContent>
          <Box className="session-status">
            <Box sx={{ display: {xs: 'none', md: 'block'}, marginLeft: '0.365rem'}}>
              <Stack direction='row' spacing={1} justifyContent={'space-between'}>
                <SessionStatusView {...props} />
                {props.isUpdating && 
                  <Stack direction='row' spacing={1} justifyContent='flex-end' alignItems='center'>
                    <CircularProgress size={20} />
                    <Typography variant='body2' color='text.gray'>Refreshing</Typography>
                  </Stack>
                }
              </Stack>
            </Box>

            <Box sx={{ display: {xs: 'block', md: 'none'}}}>
              <CustomAccordionStatusFilter
                SummaryIcon={Icon}
                summaryColor={color}
                iconUrl="/images/logo-180px.png"
                summaryName={tabName}
                count={props.counts[tab]}
                DetailComponent={() => <SessionStatusView {...props} />}
              />
            </Box>
            <Stack direction='row' justifyContent={'end'}>
              <Box sx={{ display: {xs: 'block', sm: 'none'}}}>
                <Box sx={{ marginRight: '0.875rem' }}>
                  <SortButton
                    sort={props.filters.sort}
                    onChange={sort => props.onFilterChange({ ...props.filters, sort })}
                  ></SortButton>
                </Box>
              </Box>
            </Stack>
          </Box>

          {Object.keys(props.sessions).map(k =>
            tab === k
              ? <TableView
                isPatient={props.isPatient}
                isLoading={props.isLoading}
                isUpdating={props.isUpdating}
                title={`${k.toUpperCase()[0]}${k.slice(1)}`}
                category={k}
                sessions={get(props, `sessions[${k}]`)}
                pageSize={props.pageSize}
                pageIndex={get(props, `pageIndex[${k}]`) || 0}
                count={get(props, `counts[${k}]`)}
                onPageChange={props.onPageChange(k)}
                afterSave={get(props, `queries[${k}].refetch`) || null}
                showAllProviders={props.showAllProviders}
                isMobile={isMobile}
              />
              : null
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

ListSessionsScreen.propTypes = {
  filters: PropTypes.object,
  tab: PropTypes.string,
  counts: PropTypes.object,
};

export default ListSessionsScreen;
